import React, { useCallback, useState } from "react";
import { Star } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

const API = "https://api.artamobile.id/v2";
const MAX_COMMENT = 1000;

/**
 * Star rating plus an optional comment, shared by both feedback cards.
 *
 * `kind` decides what is being rated: INSTALL for the setup process, SERVICE
 * for the connection. The comment box appears once a rating is chosen — showing
 * it upfront makes the card look like a form and costs responses.
 */
export default function SimRatingForm({ simId, kind, smdpStatus, question, existing }) {
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState("");
    const [saving, setSaving] = useState(false);
    // true once a submit fails; the message comes from the dictionary.
    const [error, setError] = useState(false);
    const [done, setDone] = useState(false);
    const { t } = useLanguage();

    const submit = useCallback(
        async (value, text) => {
            setSaving(true);
            setError(false);

            try {
                const res = await fetch(`${API}/sim/${encodeURIComponent(simId)}/review`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        kind,
                        rating: value,
                        smdpStatus: smdpStatus || undefined,
                        comment: text || undefined,
                    }),
                });

                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                setDone(true);
            } catch (e) {
                setError(true);
            } finally {
                setSaving(false);
            }
        },
        [simId, kind, smdpStatus],
    );

    if (done || existing) {
        const value = done ? rating : existing.rating;

        return (
            <div className="ed-rate ed-rate-done">
                <div className="ed-rate-stars" aria-label={t("review.doneAria", { n: value })}>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} size={16} className={n <= value ? "ed-star on" : "ed-star"} />
                    ))}
                </div>
                <span className="ed-rate-thanks">{t("review.thanks")}</span>
            </div>
        );
    }

    return (
        <div className="ed-rate">
            <p className="ed-rate-q">{question}</p>

            <div className="ed-rate-stars" role="radiogroup" aria-label={question}>
                {[1, 2, 3, 4, 5].map((n) => (
                    <button
                        key={n}
                        type="button"
                        role="radio"
                        aria-checked={rating === n}
                        aria-label={t("review.star", { n })}
                        className="ed-star-btn"
                        disabled={saving}
                        onClick={() => setRating(n)}
                        onMouseEnter={() => setHover(n)}
                        onMouseLeave={() => setHover(0)}
                    >
                        <Star size={28} className={n <= (hover || rating) ? "ed-star on" : "ed-star"} />
                    </button>
                ))}
            </div>

            {rating > 0 && (
                <>
                    <textarea
                        className="ed-rate-comment"
                        placeholder={t("review.placeholder")}
                        maxLength={MAX_COMMENT}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                    />
                    <button
                        type="button"
                        className="ed-rate-submit"
                        disabled={saving}
                        onClick={() => submit(rating, comment.trim())}
                    >
                        {saving ? t("review.sending") : t("review.submit")}
                    </button>
                </>
            )}

            {error && <p className="ed-rate-error" role="alert">{t("review.submitError")}</p>}
        </div>
    );
}
